import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Navigation, 
  MapPin, 
  Clock, 
  Route,
  Car,
  Bike,
  Footprints,
  Bus,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ExternalLink,
  Shield,
  Home,
  Heart,
  Users,
  Phone,
  Zap,
  Info,
  Target,
  Compass
} from 'lucide-react';
import toast from 'react-hot-toast';
import { EvacuationRoute, SafeZone, Coordinates } from '../../types/emergency';
import { MOCK_SAFE_ZONES } from '../../data/emergencyData';

interface EvacuationRoutesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface RouteOption {
  mode: 'driving' | 'walking' | 'cycling' | 'public';
  duration: string;
  distance: string;
  traffic: 'clear' | 'light' | 'moderate' | 'heavy' | 'blocked';
  icon: React.ComponentType<any>;
  description: string;
  difficulty: 'easy' | 'moderate' | 'difficult';
  safety: 'high' | 'medium' | 'low';
}

const EvacuationRoutesModal: React.FC<EvacuationRoutesModalProps> = ({ isOpen, onClose }) => {
  const [userLocation, setUserLocation] = useState<Coordinates | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [selectedSafeZone, setSelectedSafeZone] = useState<SafeZone | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<RouteOption | null>(null);
  const [safeZones, setSafeZones] = useState<SafeZone[]>(MOCK_SAFE_ZONES);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'routes' | 'safezones'>('routes');

  // Mock route options based on selected safe zone
  const generateRouteOptions = (destination: SafeZone): RouteOption[] => {
    const baseDistance = Math.random() * 5 + 1; // 1-6 miles
    
    return [
      {
        mode: 'driving',
        duration: `${Math.round(baseDistance * 3 + Math.random() * 5)} min`,
        distance: `${baseDistance.toFixed(1)} miles`,
        traffic: Math.random() > 0.7 ? 'moderate' : 'light',
        icon: Car,
        description: 'Fastest route via main roads',
        difficulty: 'easy',
        safety: 'high'
      },
      {
        mode: 'walking',
        duration: `${Math.round(baseDistance * 15 + Math.random() * 10)} min`,
        distance: `${(baseDistance * 0.8).toFixed(1)} miles`,
        traffic: 'clear',
        icon: Footprints,
        description: 'Direct pedestrian route through safe areas',
        difficulty: baseDistance > 3 ? 'moderate' : 'easy',
        safety: 'medium'
      },
      {
        mode: 'cycling',
        duration: `${Math.round(baseDistance * 6 + Math.random() * 5)} min`,
        distance: `${(baseDistance * 0.9).toFixed(1)} miles`,
        traffic: 'light',
        icon: Bike,
        description: 'Bike-friendly route with dedicated lanes',
        difficulty: 'moderate',
        safety: 'medium'
      },
      {
        mode: 'public',
        duration: `${Math.round(baseDistance * 8 + Math.random() * 15)} min`,
        distance: `${(baseDistance * 1.2).toFixed(1)} miles`,
        traffic: 'light',
        icon: Bus,
        description: 'Public transit with emergency service priority',
        difficulty: 'easy',
        safety: 'high'
      }
    ];
  };

  const [routeOptions, setRouteOptions] = useState<RouteOption[]>([]);

  useEffect(() => {
    if (isOpen) {
      getCurrentLocation();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedSafeZone) {
      setRouteOptions(generateRouteOptions(selectedSafeZone));
    }
  }, [selectedSafeZone]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      return;
    }

    setIsGettingLocation(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setIsGettingLocation(false);
        
        // Auto-select nearest safe zone
        const nearest = findNearestSafeZone({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        if (nearest) {
          setSelectedSafeZone(nearest);
        }
      },
      (error) => {
        setLocationError('Unable to get your location. Please enable location services.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const findNearestSafeZone = (location: Coordinates): SafeZone | null => {
    if (safeZones.length === 0) return null;
    
    let nearest = safeZones[0];
    let minDistance = calculateDistance(location, nearest.coordinates);
    
    safeZones.forEach(zone => {
      const distance = calculateDistance(location, zone.coordinates);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = zone;
      }
    });
    
    return nearest;
  };

  const calculateDistance = (point1: Coordinates, point2: Coordinates): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (point2.lat - point1.lat) * Math.PI / 180;
    const dLng = (point2.lng - point1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    
    // Simulate API call to refresh safe zone data
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Update safe zone occupancy (simulate real-time updates)
    setSafeZones(zones => zones.map(zone => ({
      ...zone,
      currentOccupancy: Math.max(0, zone.currentOccupancy + Math.floor(Math.random() * 20 - 10)),
      lastUpdated: new Date()
    })));
    
    setIsRefreshing(false);
    toast.success('Data refreshed');
  };

  const startNavigation = (route?: RouteOption) => {
    if (!selectedSafeZone) {
      toast.error('Please select a destination first');
      return;
    }

    const destination = `${selectedSafeZone.coordinates.lat},${selectedSafeZone.coordinates.lng}`;
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    let url = '';
    
    if (isMobile) {
      if (isIOS) {
        // Apple Maps for iOS
        const mode = route?.mode === 'walking' ? 'w' : route?.mode === 'cycling' ? 'b' : 'd';
        url = `maps://maps.apple.com/?daddr=${destination}&dirflg=${mode}`;
      } else {
        // Google Maps for Android
        const mode = route?.mode === 'walking' ? 'walking' : route?.mode === 'cycling' ? 'bicycling' : 'driving';
        url = `google.navigation:q=${destination}&mode=${mode}`;
      }
    } else {
      // Web-based Google Maps for desktop
      const mode = route?.mode === 'walking' ? 'walking' : route?.mode === 'cycling' ? 'bicycling' : 'driving';
      url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=${mode}`;
    }
    
    try {
      window.open(url, '_blank');
      toast.success('Opening navigation app...');
    } catch (error) {
      const fallbackUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
      window.open(fallbackUrl, '_blank');
      toast.success('Opening Google Maps...');
    }
    
    onClose();
  };

  const getTrafficColor = (traffic: string) => {
    switch (traffic) {
      case 'clear': return 'text-accent-green';
      case 'light': return 'text-accent-green';
      case 'moderate': return 'text-accent-orange';
      case 'heavy': return 'text-accent-red';
      case 'blocked': return 'text-accent-red';
      default: return 'text-gray-400';
    }
  };

  const getSafetyColor = (safety: string) => {
    switch (safety) {
      case 'high': return 'text-accent-green';
      case 'medium': return 'text-accent-orange';
      case 'low': return 'text-accent-red';
      default: return 'text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-accent-green bg-accent-green/20';
      case 'full': return 'text-accent-orange bg-accent-orange/20';
      case 'closed': return 'text-accent-red bg-accent-red/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  const getSafeZoneIcon = (type: string) => {
    switch (type) {
      case 'shelter': return Home;
      case 'hospital': return Heart;
      case 'evacuation-center': return Shield;
      case 'safe-area': return Target;
      default: return MapPin;
    }
  };

  const getOccupancyPercentage = (zone: SafeZone) => {
    return Math.round((zone.currentOccupancy / zone.capacity) * 100);
  };

  const getOccupancyColor = (percentage: number) => {
    if (percentage < 50) return 'bg-accent-green';
    if (percentage < 80) return 'bg-accent-orange';
    return 'bg-accent-red';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            className="bg-dark-800 border border-dark-600 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-700">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent-orange rounded-lg flex items-center justify-center">
                  <Navigation className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Evacuation Routes</h2>
                  <p className="text-sm text-gray-400">Find safe zones and optimal evacuation paths</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={refreshData}
                  disabled={isRefreshing}
                  className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                  title="Refresh data"
                >
                  <RefreshCw className={`w-5 h-5 text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Location Status */}
            <div className="p-6 border-b border-dark-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Compass className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-300">Your Location</span>
                </div>
                {isGettingLocation ? (
                  <div className="flex items-center space-x-2 text-accent-orange">
                    <div className="w-3 h-3 border border-accent-orange border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs">Getting location...</span>
                  </div>
                ) : userLocation ? (
                  <div className="flex items-center space-x-2 text-accent-green">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-xs">Location found</span>
                  </div>
                ) : (
                  <button
                    onClick={getCurrentLocation}
                    className="text-xs text-primary-400 hover:text-primary-300 transition-colors"
                  >
                    Enable Location
                  </button>
                )}
              </div>
              {locationError && (
                <p className="text-xs text-accent-orange mt-2">{locationError}</p>
              )}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-dark-700">
              <button
                onClick={() => setActiveTab('routes')}
                className={`flex-1 py-4 px-6 text-center transition-colors ${
                  activeTab === 'routes'
                    ? 'text-accent-orange border-b-2 border-accent-orange bg-accent-orange/5'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Route className="w-4 h-4" />
                  <span>Routes</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('safezones')}
                className={`flex-1 py-4 px-6 text-center transition-colors ${
                  activeTab === 'safezones'
                    ? 'text-primary-400 border-b-2 border-primary-400 bg-primary-400/5'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Shield className="w-4 h-4" />
                  <span>Safe Zones ({safeZones.filter(z => z.status === 'open').length})</span>
                </div>
              </button>
            </div>

            <div className="flex h-[calc(90vh-280px)]">
              {/* Left Panel - Safe Zones */}
              <div className="w-1/2 p-6 border-r border-dark-700 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Available Safe Zones</h3>
                  <span className="text-xs text-gray-400">
                    Last updated: {new Date().toLocaleTimeString()}
                  </span>
                </div>
                
                <div className="space-y-3">
                  {safeZones.map((zone) => {
                    const Icon = getSafeZoneIcon(zone.type);
                    const occupancyPercentage = getOccupancyPercentage(zone);
                    const distance = userLocation ? calculateDistance(userLocation, zone.coordinates) : null;
                    
                    return (
                      <motion.div
                        key={zone.id}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setSelectedSafeZone(zone)}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          selectedSafeZone?.id === zone.id
                            ? 'border-primary-400 bg-primary-400/10'
                            : 'border-dark-600 hover:border-dark-500 bg-dark-700/50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              selectedSafeZone?.id === zone.id ? 'bg-primary-400' : 'bg-dark-600'
                            }`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-white">{zone.name}</h4>
                              <p className="text-sm text-gray-400 capitalize">{zone.type.replace('-', ' ')}</p>
                              {distance && (
                                <p className="text-xs text-primary-400">{distance.toFixed(1)} miles away</p>
                              )}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getStatusColor(zone.status)}`}>
                            {zone.status}
                          </span>
                        </div>

                        {/* Capacity */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-400">Capacity</span>
                            <span className="text-xs text-white">
                              {zone.currentOccupancy} / {zone.capacity}
                            </span>
                          </div>
                          <div className="w-full bg-dark-700 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all ${getOccupancyColor(occupancyPercentage)}`}
                              style={{ width: `${Math.min(occupancyPercentage, 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* Amenities */}
                        <div className="flex flex-wrap gap-1">
                          {zone.amenities.slice(0, 3).map((amenity, index) => (
                            <span key={index} className="px-2 py-1 bg-dark-600 text-gray-300 rounded text-xs">
                              {amenity}
                            </span>
                          ))}
                          {zone.amenities.length > 3 && (
                            <span className="px-2 py-1 bg-dark-600 text-gray-300 rounded text-xs">
                              +{zone.amenities.length - 3} more
                            </span>
                          )}
                        </div>

                        {/* Contact */}
                        <div className="mt-2 flex items-center space-x-2 text-xs text-gray-400">
                          <Phone className="w-3 h-3" />
                          <span>{zone.contactInfo}</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Right Panel - Routes or Safe Zone Details */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {activeTab === 'routes' ? (
                  selectedSafeZone ? (
                    <div className="space-y-6">
                      {/* Destination Info */}
                      <div className="bg-dark-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-white mb-2">Destination: {selectedSafeZone.name}</h4>
                        <div className="text-sm text-gray-400 space-y-1">
                          <p>Type: {selectedSafeZone.type.replace('-', ' ')}</p>
                          <p>Status: <span className={getStatusColor(selectedSafeZone.status).split(' ')[0]}>{selectedSafeZone.status}</span></p>
                          <p>Available spaces: {selectedSafeZone.capacity - selectedSafeZone.currentOccupancy}</p>
                        </div>
                      </div>

                      {/* Route Options */}
                      <div>
                        <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                          <Route className="w-4 h-4" />
                          Route Options
                        </h4>
                        
                        <div className="space-y-3">
                          {routeOptions.map((route, index) => {
                            const Icon = route.icon;
                            return (
                              <motion.button
                                key={index}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setSelectedRoute(route)}
                                className={`w-full p-4 rounded-lg border transition-all text-left ${
                                  selectedRoute?.mode === route.mode
                                    ? 'border-primary-400 bg-primary-400/10'
                                    : 'border-dark-600 hover:border-dark-500 bg-dark-700/50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                      selectedRoute?.mode === route.mode ? 'bg-primary-400' : 'bg-dark-600'
                                    }`}>
                                      <Icon className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                      <div className="flex items-center space-x-2 mb-1">
                                        <span className="font-medium text-white capitalize">{route.mode}</span>
                                        <span className={`text-xs px-2 py-1 rounded ${getTrafficColor(route.traffic)} bg-current/10`}>
                                          {route.traffic}
                                        </span>
                                        <span className={`text-xs px-2 py-1 rounded ${getSafetyColor(route.safety)} bg-current/10`}>
                                          {route.safety} safety
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-400">{route.description}</p>
                                      <p className="text-xs text-gray-500 mt-1">Difficulty: {route.difficulty}</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-semibold text-white">{route.duration}</div>
                                    <div className="text-sm text-gray-400">{route.distance}</div>
                                  </div>
                                </div>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-3">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => startNavigation(selectedRoute)}
                          className="w-full bg-gradient-to-r from-accent-orange to-accent-red py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <Navigation className="w-5 h-5" />
                          Start Navigation
                        </motion.button>
                        
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => startNavigation()}
                          className="w-full border border-dark-600 py-3 rounded-lg font-semibold text-gray-300 hover:bg-dark-700 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <ExternalLink className="w-5 h-5" />
                          Open in Maps App
                        </motion.button>
                      </div>

                      {/* Emergency Notice */}
                      <div className="bg-accent-red/10 border border-accent-red/30 rounded-lg p-4">
                        <div className="flex items-center space-x-2 text-accent-red mb-2">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-medium">Emergency Notice</span>
                        </div>
                        <div className="text-xs text-gray-300 space-y-1">
                          <p>• Routes may change due to emergency conditions</p>
                          <p>• Follow official evacuation orders and road closures</p>
                          <p>• Keep emergency supplies and documents ready</p>
                          <p>• Stay informed through official emergency channels</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">Select a Destination</h3>
                      <p className="text-gray-400">Choose a safe zone to view available evacuation routes</p>
                    </div>
                  )
                ) : (
                  /* Safe Zone Details Tab */
                  selectedSafeZone ? (
                    <div className="space-y-6">
                      {/* Safe Zone Header */}
                      <div className="bg-dark-700/50 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-12 h-12 bg-primary-400 rounded-lg flex items-center justify-center">
                            {React.createElement(getSafeZoneIcon(selectedSafeZone.type), { className: "w-6 h-6 text-white" })}
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white">{selectedSafeZone.name}</h3>
                            <p className="text-gray-400 capitalize">{selectedSafeZone.type.replace('-', ' ')}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-400">Status:</span>
                            <span className={`ml-2 px-2 py-1 rounded text-xs ${getStatusColor(selectedSafeZone.status)}`}>
                              {selectedSafeZone.status}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-400">Contact:</span>
                            <span className="ml-2 text-white">{selectedSafeZone.contactInfo}</span>
                          </div>
                        </div>
                      </div>

                      {/* Capacity Details */}
                      <div className="bg-dark-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Capacity Information
                        </h4>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400">Total Capacity:</span>
                            <span className="text-white font-medium">{selectedSafeZone.capacity}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400">Current Occupancy:</span>
                            <span className="text-white font-medium">{selectedSafeZone.currentOccupancy}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400">Available Spaces:</span>
                            <span className="text-accent-green font-medium">
                              {selectedSafeZone.capacity - selectedSafeZone.currentOccupancy}
                            </span>
                          </div>
                          
                          <div className="w-full bg-dark-700 rounded-full h-3 mt-2">
                            <div
                              className={`h-3 rounded-full transition-all ${getOccupancyColor(getOccupancyPercentage(selectedSafeZone))}`}
                              style={{ width: `${Math.min(getOccupancyPercentage(selectedSafeZone), 100)}%` }}
                            />
                          </div>
                          <div className="text-center text-xs text-gray-400">
                            {getOccupancyPercentage(selectedSafeZone)}% occupied
                          </div>
                        </div>
                      </div>

                      {/* Amenities */}
                      <div className="bg-dark-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Available Amenities
                        </h4>
                        
                        <div className="grid grid-cols-2 gap-2">
                          {selectedSafeZone.amenities.map((amenity, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm">
                              <CheckCircle className="w-3 h-3 text-accent-green" />
                              <span className="text-gray-300">{amenity}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="space-y-3">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setActiveTab('routes');
                            setSelectedRoute(null);
                          }}
                          className="w-full bg-gradient-to-r from-primary-400 to-primary-500 py-3 rounded-lg font-semibold text-white hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <Route className="w-5 h-5" />
                          Plan Route Here
                        </motion.button>
                        
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            if (typeof window !== 'undefined') {
                              window.location.href = `tel:${selectedSafeZone.contactInfo}`;
                            }
                          }}
                          className="w-full border border-dark-600 py-3 rounded-lg font-semibold text-gray-300 hover:bg-dark-700 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <Phone className="w-5 h-5" />
                          Call Facility
                        </motion.button>
                      </div>

                      {/* Last Updated */}
                      <div className="text-center text-xs text-gray-500">
                        Last updated: {selectedSafeZone.lastUpdated.toLocaleString()}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-white mb-2">Select a Safe Zone</h3>
                      <p className="text-gray-400">Choose a safe zone to view detailed information</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EvacuationRoutesModal;